import React from "react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, KineticLines } from "@/components/motion/Reveal";
import CertBar from "@/components/CertBar";
import EditorialMarquee from "@/components/EditorialMarquee";
import SmartImg from "@/components/SmartImg";

const FACILITY = "/media/about-hero.png";

const TIMELINE = [
  { y: "1976", t: { id: "Awal Mula", en: "The Beginning" }, d: { id: "Berdiri sebagai industri rumahan yang meracik bahan kue.", en: "Founded as a home industry crafting baking ingredients." } },
  { y: "2007", t: { id: "Menjadi PT", en: "Incorporated as PT" }, d: { id: "Resmi berbadan hukum sebagai Perseroan Terbatas (PT).", en: "Officially incorporated as a Limited Liability Company (PT)." } },
  { y: { id: "Kini", en: "Now" }, t: { id: "Rumah Lima Merek", en: "A House of Five Brands" }, d: { id: "Melayani rumah tangga, HoReKa, UMKM, dan importir internasional.", en: "Serving households, businesses, and international importers." } },
];

const SUSTAIN = [
  { t: { id: "Sumber Bahan yang Efisien", en: "Efficient Sourcing" }, d: { id: "Pengadaan bahan baku yang bertanggung jawab dan efisien.", en: "Responsible, efficient sourcing of raw materials." } },
  { t: { id: "Produksi yang Efektif", en: "Effective Production" }, d: { id: "Metode produksi dan operasional yang efektif.", en: "Effective production and operational methods." } },
  { t: { id: "Solusi Nyata", en: "Concrete Solutions" }, d: { id: "Solusi konkret untuk tantangan keberlanjutan.", en: "Concrete solutions to sustainability challenges." } },
];

const SEGMENTS = [
  { id: "Distributor Lokal", en: "Local Distributors" },
  { id: "Rumah Tangga & End User", en: "Households & End Users" },
  { id: "HoReKa (Hotel/Restoran/Kafe)", en: "HoReKa (Hotel/Restaurant/Café)" },
  { id: "Bakery & UMKM", en: "Bakeries & SMEs" },
  { id: "Toko Bahan Kue", en: "Baking Ingredient Stores" },
  { id: "Importir Internasional", en: "International Importers" },
];

export default function About() {
  const { t, loc } = useLang();
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="pt-36 md:pt-48 pb-16 md:pb-24 max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="text-[11px] uppercase tracking-[0.3em] text-claris font-semibold mb-6">{t("common.since")}</div>
        <h1 className="font-serif text-6xl md:text-8xl leading-[0.9] text-ink max-w-4xl">
          <KineticLines lines={[t("about.title")]} />
        </h1>
        <p className="mt-8 max-w-2xl text-inkmute text-lg md:text-xl leading-relaxed">{t("about.intro")}</p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 mb-24">
        <Reveal>
          <div className="relative overflow-hidden aspect-[16/9]">
            <SmartImg src={FACILITY} alt="Facility" className="h-full w-full object-cover" />
          </div>
        </Reveal>
      </section>

      {/* TIMELINE */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-8 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line">
          {TIMELINE.map((tl, i) => (
            <Reveal key={tl.y} delay={i * 0.1}>
              <div className="bg-cream p-8 md:p-10 h-full">
                <div className="font-serif text-5xl md:text-6xl text-claris">{loc(tl.y)}</div>
                <h3 className="font-serif text-2xl text-ink mt-4">{loc(tl.t)}</h3>
                <p className="text-inkmute mt-3 leading-relaxed">{loc(tl.d)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <EditorialMarquee text="Established 1976" />

      {/* MISSION + SUSTAIN */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <Reveal>
            <div className="text-[11px] uppercase tracking-[0.3em] text-inkmute font-semibold mb-5">{t("about.missionTitle")}</div>
            <p className="font-serif text-3xl md:text-4xl leading-snug text-ink">{t("about.mission")}</p>
          </Reveal>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-inkmute font-semibold mb-8">{t("about.sustainTitle")}</div>
            <div className="space-y-6">
              {SUSTAIN.map((s, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="border-t border-line pt-5">
                    <h3 className="font-serif text-2xl text-ink">{loc(s.t)}</h3>
                    <p className="text-inkmute mt-2 leading-relaxed">{loc(s.d)}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SEGMENTS */}
      <section className="bg-ink text-cream py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal><h2 className="font-serif text-4xl md:text-6xl mb-12">{t("about.segmentsTitle")}</h2></Reveal>
          <div className="flex flex-wrap gap-4">
            {SEGMENTS.map((s, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <span className="inline-block border border-cream/25 rounded-full px-6 py-3 text-base md:text-lg text-cream/90">{loc(s)}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CERTS */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <Reveal className="max-w-xl mb-12">
          <h2 className="font-serif text-4xl md:text-5xl text-ink">{t("nav.certs")}</h2>
        </Reveal>
        <Reveal delay={0.1}><CertBar /></Reveal>
      </section>
    </div>
  );
}
